 
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Link, useNavigate } from "react-router-dom";

import { useFormik } from "formik";
import * as Yup from "yup";

import { Input, Label, Button } from "@/components/ui";
import { useResetPasswordMutation, useToast } from "@/hooks";
import { DisplayError } from "@/utils";

export function PasswordReset() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { toast } = useToast();

  const { mutateAsync, isLoading } = useResetPasswordMutation();

  const [initialValues, setInitialValues] = useState({
    email: "",
    token: "",
    password: "",
    confirmPassword: "",
  });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const email = params.get("email") || "";
    const token = params.get("token") || "";
    setInitialValues({ email, token, password: "", confirmPassword: "" });
  }, []);

  const newPasswordSchema = Yup.object().shape({
    email: Yup.string().required(t("LABEL.required")),
    token: Yup.string().required(t("LABEL.required")),
    password: Yup.string()
      .min(6, t("LABEL.min 6"))
      .required(t("LABEL.required")),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("password"), undefined], t("LABEL.confirm password"))
      .required(t("LABEL.required")),
  });

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: newPasswordSchema,
    onSubmit: async (values) => {
      try {
        await mutateAsync(values);
        toast({
          title: t("LABEL.success"),
          description: t("MESSAGE.password updated"),
        });

        navigate("/auth");

      } catch (error) {
        toast({
          title: t("LABEL.error"),
          description: DisplayError(error, t("MESSAGE.error_message")),
          variant: "destructive",
        });
      }
    },
  });

  return (
    <div className="max-w-[373px] w-full min-h-[596px] bg-white rounded-xl p-10 space-y-11 flex flex-col">

      <div className="flex flex-col justify-center items-center gap-4">
        <img
          width={140}
          height={70}
          src="/media/logos/logo-medium.svg"
          alt="Clinisharp logo"
          className="object-contain"
        />
        <h1 className="text-3xl font-bold text-zinc-700">{t("welcome")}</h1>
        <p className="text-sm text-app-fg-muted text-center">
          Preencha os campos abaixo para atualizar sua senha
        </p>
      </div>
      <form
        onSubmit={formik.handleSubmit}
        noValidate
        className="flex-1 flex flex-col justify-between"
      >
        <div className="flex flex-col gap-6">
          <div className="relative flex flex-col gap-2 w-full">
            <Label className="text-sm text-zinc-500 font-normal">
              {t("form.password")}
            </Label>
            <Input
              type="password"
              {...formik.getFieldProps("password")}
              isInvalid={formik.touched.password && formik.errors.password}
              isValid={formik.touched.password && !formik.errors.password}
              messageError={formik.errors.password}
            />
          </div>
          <div className="relative flex flex-col gap-1 w-full">
            <Label className="text-sm text-zinc-500 font-normal">
              {t("form.confirm password")}
            </Label>
            <div className="text-end flex flex-col gap-2">
              <Input
                type="password"
                {...formik.getFieldProps("confirmPassword")}
                isInvalid={
                  formik.touched.confirmPassword &&
                  formik.errors.confirmPassword
                }
                isValid={
                  formik.touched.confirmPassword &&
                  !formik.errors.confirmPassword
                }
                messageError={formik.errors.confirmPassword}
              />

              <Link
                to="/auth/forgot-password"
                className=" text-sm text-teal-600 font-medium"
              >
                {t("form.forgot-password")}
              </Link>
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-5">
          <Button
            size="lg"
            type="submit"
            className="w-full bg-teal-700 hover:bg-teal-800"
            disabled={isLoading}
            isLoading={isLoading}
          >
            {t("actions.reset")}
          </Button>
        </div>
      </form>
    </div>
  );
}
